import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany, THEME_PALETTES } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Building2, AlertTriangle, LogOut, ArrowLeft, CheckCircle2 } from 'lucide-react';

const CompanySelector = () => {
  const { companies, activeCompany, selectCompany } = useCompany();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [pendingCompany, setPendingCompany] = useState(null);

  // Direct select (login flow / re-selecting the same company): no confirmation needed
  const handleSelect = (company) => {
    if (activeCompany && company.company_id !== activeCompany.company_id) {
      // Switching to a DIFFERENT company -> confirm first
      setPendingCompany(company);
      return;
    }
    selectCompany(company);
    navigate('/dashboard', { replace: true });
  };

  // Confirmed switch: select + full reload so ALL company-scoped data reloads fresh
  const confirmSwitch = () => {
    if (!pendingCompany) return;
    selectCompany(pendingCompany); // clears filters + updates company context
    setPendingCompany(null);
    // Hard navigation to Dashboard guarantees dashboard/reports/notifications/audit reload
    window.location.href = '/dashboard';
  };

  // No companies assigned — show helpful message
  if (companies.length === 0) {
    return (
      <div data-testid="company-selector-page" className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-md mx-4 text-center">
          <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <h1 data-testid="no-company-title" className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            No Company Access
          </h1>
          <p className="text-slate-500 mb-6">
            Your account has not been assigned to any company yet. Please contact your administrator to get access.
          </p>
          <Button data-testid="no-company-logout-btn" onClick={logout} variant="outline" className="gap-2">
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="company-selector-page" className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-3xl mx-4">
        {/* Back to dashboard when switching from within the app */}
        {activeCompany && (
          <button
            data-testid="company-selector-back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </button>
        )}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <h1 data-testid="company-selector-title" className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {activeCompany ? 'Switch Company' : 'Select Company'}
          </h1>
          <p className="text-slate-500 mt-2">Choose which company you want to work in</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {companies.map((company) => {
            const palette = THEME_PALETTES[company.theme_color] || THEME_PALETTES.teal;
            const isCurrent = activeCompany?.company_id === company.company_id;
            return (
              <Card
                key={company.company_id}
                data-testid={`company-card-${company.code}`}
                onClick={() => handleSelect(company)}
                className={`cursor-pointer border-2 hover:shadow-lg transition-all group ${isCurrent ? '' : 'border-slate-200'}`}
                style={isCurrent ? { borderColor: palette[500] } : { '--hover-border': palette[500] }}
                onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.borderColor = palette[500]; }}
                onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.borderColor = ''; }}
              >
                <CardContent className="p-6 text-center relative">
                  {isCurrent && (
                    <span data-testid="current-company-marker" className="absolute top-2 right-2 flex items-center gap-1 text-xs font-medium" style={{ color: palette[700] }}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Current
                    </span>
                  )}
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 transition-colors"
                    style={{ backgroundColor: palette[50] }}
                  >
                    <span className="text-lg font-bold" style={{ color: palette[700] }}>
                      {company.code}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{company.name}</h3>
                  <div className="flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: palette[500] }}></span>
                    <Badge variant="outline" className="text-xs">
                      {company.role}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Switch confirmation */}
      <AlertDialog open={!!pendingCompany} onOpenChange={(o) => !o && setPendingCompany(null)}>
        <AlertDialogContent data-testid="switch-company-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to {pendingCompany?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              All active filters will be cleared and you will be taken to the Dashboard of the selected company.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="switch-company-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="switch-company-confirm-btn" onClick={confirmSwitch} className="bg-slate-900 hover:bg-slate-800">
              Switch Company
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanySelector;
